import React, { useId } from 'react'
import type { LoyaltyCouponLimits } from '../../../../../types/loyaltyCoupon'
import * as s from './limitsStyles'

// Mirrors `breadstack-loyalty-pro.html` lines 1083-1119. Only `perPerson`
// (per-member cap) and `total` (overall cap) are wired today — the design's
// `perDay` cap and `frequency` selector are commented out until the coupon
// backend ships matching fields.

interface Props {
  limits: LoyaltyCouponLimits
  onChange: (next: LoyaltyCouponLimits) => void
}

const parseIntOrNull = (raw: string): number | null => {
  const trimmed = raw.trim()
  if (!trimmed) return null
  const n = Number.parseInt(trimmed, 10)
  return Number.isFinite(n) && n >= 0 ? n : null
}

export const CouponUsageLimitsBlock: React.FC<Props> = ({
  limits,
  onChange,
}) => {
  const idPerPerson = useId()
  const idTotal = useId()
  return (
    <div style={s.block}>
      <div style={s.blockHead}>
        <div>
          <div style={s.blockTitle}>Usage limits</div>
          <div style={s.blockDesc}>
            Cap how often this coupon can be redeemed
          </div>
        </div>
      </div>
      <div style={s.blockBody}>
        <div style={s.formRow}>
          <div style={s.formGroup}>
            <label style={s.formLabel} htmlFor={idPerPerson}>
              Max uses per member
            </label>
            <input
              id={idPerPerson}
              style={s.formInput}
              type="number"
              min={0}
              placeholder="Unlimited"
              value={limits.perPerson ?? ''}
              onChange={(e) =>
                onChange({
                  ...limits,
                  perPerson: parseIntOrNull(e.target.value),
                })
              }
            />
            <div style={s.formHint}>
              Leave blank for unlimited per-member uses
            </div>
          </div>
          <div style={s.formGroup}>
            <label style={s.formLabel} htmlFor={idTotal}>
              Max total uses
            </label>
            <input
              id={idTotal}
              style={s.formInput}
              type="number"
              min={0}
              placeholder="Unlimited"
              value={limits.total ?? ''}
              onChange={(e) =>
                onChange({
                  ...limits,
                  total: parseIntOrNull(e.target.value),
                })
              }
            />
            <div style={s.formHint}>
              Stops redeeming after this many across all members
            </div>
          </div>
        </div>
        {/* TODO: re-enable when backend ships `usage_limit_per_day` + `frequency`.
        <div style={s.formRow}>
          <div style={s.formGroup}>
            <label style={s.formLabel}>Max uses per day</label>
            <input style={s.formInput} type="number" min={0} placeholder="No daily cap"
              value={limits.perDay ?? ''} onChange={...} />
          </div>
          <div style={s.formGroup}>
            <label style={s.formLabel}>Frequency per member</label>
            <select style={s.formInput} value={limits.frequency} onChange={...}>
              <option value="any">Anytime (within limits)</option>
              <option value="day">Max once per day</option>
              <option value="week">Max once per week</option>
              <option value="month">Max once per month</option>
            </select>
          </div>
        </div>
        */}
      </div>
    </div>
  )
}
